/**
 * Where the API lives.
 *
 * In development the base URL is the Vite proxy (`/api`), so no CORS and no
 * hard-coded host. A production web build uses `VITE_API_URL`. The Electron
 * shell serves the same bundle but injects a config object at runtime, which is
 * why it wins: it lets one build talk to any deployment.
 *
 * Auth is two headers: the shared `API_KEY` and the `X-User-Id` obtained by
 * redeeming a link code from the Telegram bot. Both live in localStorage, which
 * is acceptable here because the desktop shell and the browser tab are the only
 * places the key exists; it is not a bearer token for a third-party app.
 */

import type {
  CalendarEvent,
  Category,
  EventAttachment,
  EventFilter,
  EventInput,
  User,
} from "@calendar/shared";

declare global {
  interface Window {
    __CALENDAR_CONFIG__?: { apiUrl?: string; apiKey?: string };
  }
}

const injected = (): { apiUrl?: string; apiKey?: string } =>
  (typeof window !== "undefined" && window.__CALENDAR_CONFIG__) || {};

const BASE = (
  injected().apiUrl ??
  import.meta.env.VITE_API_URL ??
  "/api"
).replace(/\/$/, "");

const KEY_STORAGE = "calendar.apiKey";
const USER_STORAGE = "calendar.userId";

/**
 * A key baked into the shell's config file saves the user typing it on the
 * linking screen, but it never silently overwrites one already in storage.
 */
function seedApiKey(): void {
  const key = injected().apiKey;
  if (key && !localStorage.getItem(KEY_STORAGE)) {
    localStorage.setItem(KEY_STORAGE, key.trim());
  }
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/**
 * The deployment's shared API key, if one has been seeded, or "" otherwise.
 *
 * The desktop shell injects it through its config file, so the pairing form can
 * open with the field already filled in and the user only types the code. In a
 * browser tab there is nothing to seed and this is "".
 */
export function seededApiKey(): string {
  seedApiKey();
  return localStorage.getItem(KEY_STORAGE) ?? "";
}

export function getCredentials(): { apiKey: string; userId: string } | null {
  const apiKey = seededApiKey();
  const userId = localStorage.getItem(USER_STORAGE) ?? "";
  return apiKey && userId ? { apiKey, userId } : null;
}

export function setCredentials(apiKey: string, userId: string): void {
  localStorage.setItem(KEY_STORAGE, apiKey.trim());
  localStorage.setItem(USER_STORAGE, userId.trim());
}

export function clearCredentials(): void {
  localStorage.removeItem(KEY_STORAGE);
  localStorage.removeItem(USER_STORAGE);
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  auth = true,
): Promise<T> {
  const headers: Record<string, string> = {
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (auth) {
    const creds = getCredentials();
    if (!creds) throw new ApiError(401, "no_credentials", "Not linked yet");
    headers["X-Api-Key"] = creds.apiKey;
    headers["X-User-Id"] = creds.userId;
  }
  if (init.body && !(init.body instanceof FormData) && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  const text = await res.text();
  const body = text ? (JSON.parse(text) as unknown) : null;

  if (!res.ok) {
    const err = (body ?? {}) as { error?: string; message?: string };
    if (res.status === 401 || res.status === 403) clearCredentials();
    throw new ApiError(res.status, err.error ?? "http_error", err.message ?? res.statusText);
  }
  return body as T;
}

// ---------------------------------------------------------------------------

export const api = {
  market: (day?: string) => request<MarketResponse>(`/market${day ? `?day=${encodeURIComponent(day)}` : ''}`),
  records: (id: string) => request<{items: MeetingRecord[]}>(`/events/${encodeURIComponent(id)}/records`),
  addRecord: (id: string, kind: string, body: string) => request<{items: MeetingRecord[]}>(`/events/${encodeURIComponent(id)}/records`, {method: 'POST', body: JSON.stringify({kind,body})}),
  /**
   * Redeem the 8-character code from the Telegram bot.
   *
   * The key travels in the header rather than the body so the server can reject
   * a caller that does not hold it before it spends the code.
   */
  async claim(apiKey: string, code: string): Promise<User> {
    const { user } = await request<{ user: User }>(
      "/auth/claim",
      {
        method: "POST",
        body: JSON.stringify({ code: code.trim().toUpperCase() }),
        headers: { "X-Api-Key": apiKey.trim() },
      },
      false,
    );
    setCredentials(apiKey, user.id);
    return user;
  },

  me: () => request<{ user: User }>("/me"),

  updatePreferences: (patch: Partial<Pick<User, "lang" | "timeZone" | "calendar" | "displayName">>) =>
    request<{ user: User }>("/me", { method: "PATCH", body: JSON.stringify(patch) }),

  listEvents: (filter: EventFilter = {}) => {
    const q = new URLSearchParams();
    if (filter.from) q.set("from", filter.from);
    if (filter.to) q.set("to", filter.to);
    if (filter.query) q.set("q", filter.query);
    if (filter.statuses?.length) q.set("status", filter.statuses.join(","));
    if (filter.limit) q.set("limit", String(filter.limit));
    const qs = q.toString();
    return request<{ items: CalendarEvent[]; total: number }>(
      `/events${qs ? `?${qs}` : ""}`,
    );
  },

  getEvent: (id: string) => request<CalendarEvent>(`/events/${encodeURIComponent(id)}`),

  createEvent: (input: EventInput) =>
    request<CalendarEvent>("/events", { method: "POST", body: JSON.stringify(input) }),

  updateEvent: (id: string, patch: Partial<EventInput> & { status?: string }) =>
    request<CalendarEvent>(`/events/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteEvent: (id: string) =>
    request<{ id: string }>(`/events/${encodeURIComponent(id)}`, { method: "DELETE" }),

  searchNotes: (q: string) =>
    request<{ items: CalendarEvent[] }>(`/notes?q=${encodeURIComponent(q)}`),

  listCategories: () => request<{ items: Category[] }>("/categories"),

  /** Mint a pairing code that the Telegram bot shows the user. */
  createLinkCode: () =>
    request<{ code: string; expiresAt: string }>("/me/link-code", { method: "POST" }),

  createCategory: (name: string, color: string) =>
    request<Category>("/categories", { method: "POST", body: JSON.stringify({ name, color }) }),

  updateCategory: (id: string, patch: Partial<Pick<Category, "name" | "color">>) =>
    request<Category>(`/categories/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    }),

  deleteCategory: (id: string) =>
    request<{ id: string }>(`/categories/${encodeURIComponent(id)}`, { method: "DELETE" }),

  async uploadAttachment(eventId: string, file: File): Promise<EventAttachment> {
    const form = new FormData();
    form.append("file", file);
    return request<EventAttachment>(
      `/events/${encodeURIComponent(eventId)}/attachments`,
      { method: "POST", body: form },
    );
  },

  deleteAttachment: (id: string) =>
    request<{ id: string }>(`/attachments/${encodeURIComponent(id)}`, { method: "DELETE" }),

  /**
   * Fetch an attachment body.
   *
   * Attachments are private, so `<img src>` and `<a href>` cannot be used
   * directly: the request has to carry the auth headers. Callers turn the blob
   * into an object URL instead.
   */
  async fetchAttachment(url: string): Promise<Blob> {
    const creds = getCredentials();
    if (!creds) throw new ApiError(401, "no_credentials", "Not linked yet");
    const res = await fetch(url, {
      headers: { "X-Api-Key": creds.apiKey, "X-User-Id": creds.userId },
    });
    if (!res.ok) throw new ApiError(res.status, "file_error", res.statusText);
    return res.blob();
  },

  /** Bumped by the API on every write, so the UI can poll instead of a socket. */
  revision: () => request<{ revision: number }>("/sync/revision"),
};

export interface MeetingRecord { id: string; kind: string; body: string; createdAt: string }
export interface MarketResponse {
  day: string; today: string; archive: boolean;
  items: {day: string; symbol: string; value: number; unit: string; source: string; observedAt: string}[];
}
